import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { BookOpen } from 'lucide-react';
import {
  DescriptionText,
  HorizontalStack,
  PaneHeader,
  PaneSection,
  PropertyGrid,
  PropertyItem,
  RegistryEmptyState,
  SilkInput,
  Surface,
  VerticalStack,
} from '@workspace/silk-ui';

import { Button } from '@/components/ui/button';
import { getPortalHelpArticle, getPortalHelpArticles } from '@/views/help/portalHelpContent';

export default function PortalHelpOverlay({
  articleId,
  onClose,
  onSelectArticle,
  onOpenArticle,
}: {
  articleId: string;
  onClose: () => void;
  onSelectArticle: (articleId: string) => void;
  onOpenArticle: (articleId: string) => void;
}) {
  const [query, setQuery] = useState('');
  const article = getPortalHelpArticle(articleId);
  const articles = useMemo(() => getPortalHelpArticles(), []);

  useEffect(() => {
    setQuery('');
  }, [articleId]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const filteredSections = useMemo(() => {
    if (!article) return [];
    const term = query.trim().toLowerCase();
    if (!term) return article.sections;
    return article.sections.filter(section =>
      [section.title, ...section.paragraphs].join(' ').toLowerCase().includes(term)
    );
  }, [article, query]);

  const searchMatchedArticles = useMemo(() => {
    if (!article) return [];
    const term = query.trim().toLowerCase();
    if (!term) return [];
    return articles.filter(candidate => {
      if (candidate.articleId === article.articleId) return false;
      return [candidate.title, candidate.summary, ...candidate.keywords].join(' ').toLowerCase().includes(term);
    });
  }, [article, articles, query]);

  const scrollToSection = (sectionId: string) => {
    document.getElementById(`overlay-section-${sectionId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (typeof document === 'undefined' || !article) {
    return null;
  }

  const isSearching = query.trim().length > 0;

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close help"
        className="absolute inset-0 bg-background/70 backdrop-blur-[2px]"
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${article.title} help`}
        className="relative h-full max-h-[calc(100dvh-2rem)] w-full max-w-6xl"
      >
        <Surface className="flex h-full min-h-0 flex-col overflow-hidden border-border/60 shadow-2xl">
          <PaneHeader
            title={article.title}
            subtitle="Portal Help"
            onClear={onClose}
            badges={
              <HorizontalStack gap={2} wrap>
                {article.metrics?.map(metric => (
                  <span
                    key={metric.label}
                    className="rounded-md border border-border/60 bg-background px-2 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-muted-foreground"
                  >
                    {metric.label}: {metric.value}
                  </span>
                )) ?? null}
              </HorizontalStack>
            }
          />

          <div className="grid min-h-0 flex-1 gap-0 lg:grid-cols-[280px_minmax(0,1fr)]">
            {/* Sidebar — independently scrollable */}
            <div className="flex flex-col gap-4 overflow-y-auto border-b border-border/40 bg-muted/10 p-5 lg:border-b-0 lg:border-r">
              <VerticalStack gap={2}>
                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-muted-foreground/60">
                  Search This Article
                </p>
                <SilkInput
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  placeholder="Search this topic..."
                />
              </VerticalStack>

              <Surface className="border-border/50 bg-background p-4">
                <VerticalStack gap={2}>
                  <p className="text-[10px] font-black uppercase tracking-[0.24em] text-muted-foreground/60">
                    What This Covers
                  </p>
                  <DescriptionText className="text-xs leading-6">{article.summary}</DescriptionText>
                </VerticalStack>
              </Surface>

              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={() => onOpenArticle(article.articleId)}>
                  Open Full Article
                </Button>
                {article.relatedRoutes[0] ? (
                  <Button type="button" variant="outline" onClick={onClose}>
                    Return to Workspace
                  </Button>
                ) : null}
              </div>

              {/* On this page: section jump links when not searching */}
              {!isSearching && article.sections.length > 1 && (
                <VerticalStack gap={2}>
                  <p className="text-[10px] font-black uppercase tracking-[0.24em] text-muted-foreground/60">
                    On This Page
                  </p>
                  <nav className="flex flex-col gap-0.5">
                    {article.sections.map(section => (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => scrollToSection(section.id)}
                        className="rounded px-2 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground"
                      >
                        {section.title}
                      </button>
                    ))}
                  </nav>
                </VerticalStack>
              )}

              {/* Related articles matching the search query */}
              {isSearching && searchMatchedArticles.length > 0 && (
                <VerticalStack gap={2}>
                  <p className="text-[10px] font-black uppercase tracking-[0.24em] text-muted-foreground/60">
                    Related Topics
                  </p>
                  <VerticalStack gap={2}>
                    {searchMatchedArticles.map(candidate => (
                      <button
                        key={candidate.articleId}
                        type="button"
                        onClick={() => onSelectArticle(candidate.articleId)}
                        className="rounded-md border border-border/60 bg-background px-3 py-3 text-left transition-all hover:bg-muted/20"
                      >
                        <p className="text-xs font-black text-foreground">{candidate.title}</p>
                        <DescriptionText className="mt-1 text-[11px] leading-5">{candidate.summary}</DescriptionText>
                      </button>
                    ))}
                  </VerticalStack>
                </VerticalStack>
              )}

              {/* Related articles from article data when not searching */}
              {!isSearching && article.relatedArticleIds.length > 0 && (
                <VerticalStack gap={2}>
                  <p className="text-[10px] font-black uppercase tracking-[0.24em] text-muted-foreground/60">
                    Related Topics
                  </p>
                  <VerticalStack gap={2}>
                    {article.relatedArticleIds.map(relatedId => {
                      const related = getPortalHelpArticle(relatedId);
                      if (!related) return null;
                      return (
                        <button
                          key={relatedId}
                          type="button"
                          onClick={() => onSelectArticle(relatedId)}
                          className="rounded-md border border-border/60 bg-background px-3 py-3 text-left transition-all hover:bg-muted/20"
                        >
                          <p className="text-xs font-black text-foreground">{related.title}</p>
                          <DescriptionText className="mt-1 text-[11px] leading-5">{related.summary}</DescriptionText>
                        </button>
                      );
                    })}
                  </VerticalStack>
                </VerticalStack>
              )}
            </div>

            {/* Main content panel */}
            <div className="min-h-0 overflow-y-auto p-5">
              <VerticalStack gap={4}>
                {article.metrics?.length ? (
                  <Surface className="border-border/60 p-5">
                    <VerticalStack gap={4}>
                      <p className="text-[10px] font-black uppercase tracking-[0.24em] text-muted-foreground/60">
                        At a Glance
                      </p>
                      <PropertyGrid cols={3}>
                        {article.metrics.map(metric => (
                          <PropertyItem key={metric.label} label={metric.label} value={metric.value} mono={false} />
                        ))}
                      </PropertyGrid>
                      <DescriptionText className="text-sm leading-6 text-foreground/80">{article.summary}</DescriptionText>
                    </VerticalStack>
                  </Surface>
                ) : null}

                {filteredSections.length ? (
                  <Surface className="divide-y divide-border/30 border-border/60">
                    {filteredSections.map(section => (
                      <div
                        key={section.id}
                        id={`overlay-section-${section.id}`}
                        className="scroll-mt-4 p-5"
                      >
                        <PaneSection label={section.title}>
                          <VerticalStack gap={3}>
                            {section.paragraphs.map((paragraph, index) => (
                              <DescriptionText
                                key={`${section.id}-${index}`}
                                className="text-sm leading-6 text-foreground/80"
                              >
                                {paragraph}
                              </DescriptionText>
                            ))}
                          </VerticalStack>
                        </PaneSection>
                      </div>
                    ))}
                  </Surface>
                ) : (
                  <RegistryEmptyState
                    icon={BookOpen}
                    title="No matching help topics"
                    description="Clear the filter or open the full article to keep reading."
                  />
                )}
              </VerticalStack>
            </div>
          </div>
        </Surface>
      </div>
    </div>,
    document.body
  );
}
